import { useAuthenticator } from '@aws-amplify/ui-react';
import { Button, IconButton, Spinner } from '@nrg/design-system';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuCheck, LuCopy } from 'react-icons/lu';
import { cognitoErrorAuthKey } from '@/auth/cognito-error-message';
import AuthFooterLink from '@/auth/components/AuthFooterLink';
import AuthScreen from '@/auth/components/AuthScreen';
import CodeField from '@/auth/components/CodeField';

const formatSecret = (secret: string) =>
  secret
    .replace(/\s+/g, '')
    .toUpperCase()
    .match(/.{1,4}/g)
    ?.join(' ') ?? secret;

export default function SetupTotpScreen() {
  const { t } = useTranslation('auth');
  const { isPending, error, totpSecretCode, submitForm, signOut } = useAuthenticator((c) => [
    c.isPending,
    c.error,
    c.totpSecretCode,
    c.route,
  ]);

  const [code, setCode] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [copied, setCopied] = useState(false);

  const codeError = submitAttempted && code.length !== 6 ? t('errors.code_six_digits') : undefined;
  const cognitoKey = cognitoErrorAuthKey(error);
  const banner = cognitoKey ? t(cognitoKey) : null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitAttempted(true);
    if (code.length !== 6) return;
    submitForm({ confirmation_code: code });
  };

  const handleCopy = async () => {
    if (!totpSecretCode) return;
    try {
      await navigator.clipboard.writeText(totpSecretCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <AuthScreen
      heading={t('setup_totp.heading')}
      subhead={t('setup_totp.subhead')}
      error={banner}
      onSubmit={handleSubmit}
      footer={<AuthFooterLink onClick={() => signOut()}>{t('common.sign_out')}</AuthFooterLink>}
    >
      {totpSecretCode ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--nrg-space-1-5)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--nrg-font-size-label-md)',
              letterSpacing: 'var(--nrg-letter-spacing-wider)',
              textTransform: 'uppercase',
              color: 'var(--nrg-color-text-strong)',
              fontWeight: 'var(--nrg-font-weight-semibold)',
            }}
          >
            {t('setup_totp.secret_label')}
          </span>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--nrg-space-2)',
              padding: 'var(--nrg-space-3) var(--nrg-space-4)',
              background: 'var(--nrg-color-bg-subtle)',
              border: '1px solid var(--nrg-color-border)',
              borderRadius: 'var(--nrg-radius-sm)',
              fontFamily: 'var(--nrg-font-display)',
              fontSize: 'var(--nrg-font-size-body-md)',
              letterSpacing: 'var(--nrg-letter-spacing-wider)',
              color: 'var(--nrg-color-text-strong)',
              userSelect: 'all',
            }}
          >
            <span style={{ flex: 1, wordBreak: 'break-all' }}>{formatSecret(totpSecretCode)}</span>
            <IconButton
              type="button"
              variant="ghost"
              size="sm"
              label={copied ? t('common.secret_copied') : t('common.copy_secret')}
              onClick={handleCopy}
            >
              {copied ? <LuCheck aria-hidden /> : <LuCopy aria-hidden />}
            </IconButton>
          </div>
          <span
            style={{
              fontSize: 'var(--nrg-font-size-body-sm)',
              color: 'var(--nrg-color-text-muted)',
            }}
          >
            {t('setup_totp.secret_hint')}
          </span>
        </div>
      ) : null}
      <CodeField
        label={t('confirm_sign_in.subhead_totp')}
        value={code}
        onChange={setCode}
        error={codeError}
        disabled={isPending}
      />
      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        disabled={isPending}
        iconLeft={isPending ? <Spinner size="sm" /> : undefined}
      >
        {t('setup_totp.submit')}
      </Button>
    </AuthScreen>
  );
}
